<template>
  <div class="video-boost-stat-page">
    <section class="stat-toolbar">
      <div class="toolbar-title">
        <h2>{{ isGameMode ? '视频号加热游戏看板' : '视频号加热账号看板' }}</h2>
        <span>数据来自视频号加热官方订单接口</span>
      </div>
      <div class="filter-row">
        <el-date-picker
            v-model="filters.dateRange"
            type="daterange"
            size="mini"
            unlink-panels
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="timestamp"
            class="date-filter">
        </el-date-picker>
        <el-select v-model="filters.owner" size="mini" filterable clearable placeholder="负责人" class="filter-control" @change="handleOwnerChange">
          <el-option v-for="item in ownerOptions" :key="item" :label="item" :value="item"></el-option>
        </el-select>
        <el-select v-model="filters.account" size="mini" filterable clearable placeholder="账号" class="filter-control">
          <el-option v-for="item in filteredAccountOptions" :key="item" :label="item" :value="item"></el-option>
        </el-select>
        <el-select v-model="filters.game" size="mini" filterable clearable allow-create placeholder="游戏" class="filter-control">
          <el-option v-for="item in gameOptions" :key="item" :label="item" :value="item"></el-option>
        </el-select>
        <el-select v-model="filters.gameType" size="mini" filterable clearable placeholder="品类" class="filter-control">
          <el-option v-for="item in gameTypeOptions" :key="item" :label="item" :value="item"></el-option>
        </el-select>
        <el-select v-model="filters.status" size="mini" placeholder="订单状态" class="status-filter">
          <el-option label="全部状态" :value="0"></el-option>
          <el-option label="待支付" :value="1"></el-option>
          <el-option label="加热中" :value="2"></el-option>
          <el-option label="已结束" :value="4"></el-option>
          <el-option label="审核未过" :value="6"></el-option>
        </el-select>
        <el-input v-model.trim="filters.keyword" size="mini" clearable placeholder="账号、游戏或账号ID" class="keyword-filter" @keyup.enter.native="queryData"></el-input>
        <el-button type="primary" size="mini" icon="el-icon-search" :loading="loading" @click="queryData">查询</el-button>
        <el-button size="mini" icon="el-icon-refresh-left" @click="resetFilters">重置</el-button>
        <el-button size="mini" icon="el-icon-setting" @click="openOwnerDialog">负责人配置</el-button>
        <el-button size="mini" icon="el-icon-download" :disabled="!rows.length" @click="exportRows">导出</el-button>
      </div>
    </section>

    <el-alert
        v-if="meta.failedAccountCount"
        class="failure-alert"
        type="warning"
        :closable="false"
        show-icon
        :title="'有 ' + meta.failedAccountCount + ' 个账号查询失败，本次汇总保留其余账号数据'"
        :description="failedAccountText">
    </el-alert>

    <section class="metric-grid">
      <div class="metric-item">
        <span>{{ isGameMode ? '游戏数' : '账号数' }}</span>
        <strong>{{ isGameMode ? numberText(summary.gameCount, 0) : numberText(summary.accountCount, 0) }}</strong>
      </div>
      <div class="metric-item">
        <span>订单数</span>
        <strong>{{ numberText(summary.orderCount, 0) }}</strong>
      </div>
      <div class="metric-item">
        <span>订单总预算</span>
        <strong>{{ moneyText(summary.orderBudget) }}</strong>
      </div>
      <div class="metric-item metric-blue">
        <span>账面消耗</span>
        <strong>{{ moneyText(summary.bookCost) }}</strong>
      </div>
      <div class="metric-item metric-orange">
        <span>实际消耗</span>
        <strong>{{ moneyText(summary.actualCost) }}</strong>
      </div>
      <div class="metric-item metric-green">
        <span>实时利润</span>
        <strong>{{ moneyText(summary.realProfit) }}</strong>
      </div>
    </section>

    <section class="stat-table-section">
      <div class="table-meta">
        <span>在线账号 {{ numberText(meta.onlineAccountCount, 0) }} 个，已查询 {{ numberText(meta.successfulAccountCount, 0) }} 个</span>
        <span>官方订单 {{ numberText(meta.orderCount, 0) }} 条，结果缓存 {{ numberText(meta.cacheSeconds, 0) }} 秒</span>
      </div>
      <div class="table-body">
        <el-table
          :data="pagedRows"
          border
          stripe
          size="mini"
          height="100%"
          v-loading="loading"
          element-loading-text="正在查询在线账号及官方订单"
          :summary-method="tableSummary"
          show-summary
          @sort-change="handleSortChange">
        <el-table-column v-if="!isGameMode" type="index" label="序号" width="62" align="center" :index="rowIndex"></el-table-column>
        <template v-if="isGameMode">
          <el-table-column prop="date" label="日期" width="105" fixed="left" sortable="custom"></el-table-column>
          <el-table-column prop="gameName" label="游戏" min-width="150" fixed="left" show-overflow-tooltip></el-table-column>
          <el-table-column prop="gameType" label="品类" min-width="100" show-overflow-tooltip></el-table-column>
          <el-table-column prop="ownerName" label="负责人" min-width="120" show-overflow-tooltip></el-table-column>
          <el-table-column prop="accountCount" label="账号数" width="80" align="right" sortable="custom"></el-table-column>
          <el-table-column prop="accountName" label="投放账号" min-width="230" show-overflow-tooltip>
            <template slot-scope="scope">
              <div v-if="splitAccountNames(scope.row.accountName).length" class="link-list-cell">
                <button
                    v-for="accountName in splitAccountNames(scope.row.accountName)"
                    :key="accountName"
                    type="button"
                    class="link-cell"
                    :title="'查看账号数据：' + accountName"
                    @click.stop="goToAccountStats(accountName)">
                  {{ accountName }}
                </button>
              </div>
              <span v-else>-</span>
            </template>
          </el-table-column>
        </template>
        <template v-else>
          <el-table-column prop="accountName" label="账号" min-width="150" fixed="left" show-overflow-tooltip></el-table-column>
          <el-table-column prop="ownerName" label="负责人" min-width="100" show-overflow-tooltip></el-table-column>
          <el-table-column prop="corporateAccountId" label="账号ID" min-width="150" show-overflow-tooltip></el-table-column>
          <el-table-column prop="gameName" label="游戏" min-width="150" show-overflow-tooltip>
            <template slot-scope="scope">
              <button
                  v-if="isValidJumpValue(scope.row.gameName)"
                  type="button"
                  class="link-cell"
                  :title="'查看游戏数据：' + scope.row.gameName"
                  @click.stop="goToGameStats(scope.row.gameName)">
                {{ scope.row.gameName }}
              </button>
              <span v-else>{{ scope.row.gameName || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="gameType" label="品类" min-width="100" show-overflow-tooltip></el-table-column>
          <el-table-column prop="accountBalance" label="账户余额" min-width="105" align="right" sortable="custom">
            <template slot-scope="scope">{{ moneyText(scope.row.accountBalance) }}</template>
          </el-table-column>
        </template>
        <el-table-column prop="orderCount" label="订单量" width="86" align="right" sortable="custom">
          <template slot-scope="scope">
            <button
                v-if="isGameMode && canJumpToOrderDashboard(scope.row)"
                type="button"
                class="link-cell order-count-link"
                :title="'查看订单数据：' + scope.row.gameName"
                @click.stop="goToOrderDashboard(scope.row)">
              {{ numberText(scope.row.orderCount, 0) }}
            </button>
            <span v-else>{{ numberText(scope.row.orderCount, 0) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="orderBudget" label="订单总预算" min-width="112" align="right" sortable="custom">
          <template slot-scope="scope">{{ moneyText(scope.row.orderBudget) }}</template>
        </el-table-column>
        <el-table-column prop="bookCost" label="账面消耗" min-width="105" align="right" sortable="custom">
          <template slot-scope="scope">{{ moneyText(scope.row.bookCost) }}</template>
        </el-table-column>
        <el-table-column prop="costProgress" label="消耗进度" min-width="145" align="right" sortable="custom">
          <template slot-scope="scope">
            <div class="progress-cell">
              <el-progress
                  :percentage="progressPercentage(scope.row.costProgress)"
                  :stroke-width="8"
                  :show-text="false"
                  :color="progressColor(scope.row.costProgress)">
              </el-progress>
              <span>{{ percentText(scope.row.costProgress) }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="actualCost" label="实际消耗" min-width="105" align="right" sortable="custom">
          <template slot-scope="scope">{{ moneyText(scope.row.actualCost) }}</template>
        </el-table-column>
        <el-table-column v-if="!isGameMode" prop="averageBid" label="平均出价" min-width="100" align="right" sortable="custom">
          <template slot-scope="scope">{{ moneyText(scope.row.averageBid) }}</template>
        </el-table-column>
        <el-table-column prop="activationCost" label="激活成本" min-width="100" align="right" sortable="custom">
          <template slot-scope="scope">{{ moneyText(scope.row.activationCost) }}</template>
        </el-table-column>
        <el-table-column prop="firstDayArpu" label="首日ARPU" min-width="100" align="right" sortable="custom">
          <template slot-scope="scope">{{ moneyText(scope.row.firstDayArpu) }}</template>
        </el-table-column>
        <el-table-column prop="firstDayMonetizationAmount" label="首日变现金额" min-width="115" align="right" sortable="custom">
          <template slot-scope="scope">{{ moneyText(scope.row.firstDayMonetizationAmount) }}</template>
        </el-table-column>
        <el-table-column prop="firstDayRoiAdjusted" label="首日ROI（赔付后）" min-width="135" align="right" sortable="custom">
          <template slot-scope="scope">{{ roiText(scope.row.firstDayRoiAdjusted) }}</template>
        </el-table-column>
        <el-table-column prop="realtimeRoi" label="实时变现ROI" min-width="115" align="right" sortable="custom">
          <template slot-scope="scope">{{ roiText(scope.row.realtimeRoi) }}</template>
        </el-table-column>
        <el-table-column prop="voucherAmount" label="赔付券" min-width="95" align="right" sortable="custom">
          <template slot-scope="scope">{{ moneyText(scope.row.voucherAmount) }}</template>
        </el-table-column>
        <el-table-column prop="adUsers" label="广告变现人数" min-width="115" align="right" sortable="custom"></el-table-column>
        <el-table-column prop="adIncome" label="广告变现金额" min-width="115" align="right" sortable="custom">
          <template slot-scope="scope">{{ moneyText(scope.row.adIncome) }}</template>
        </el-table-column>
        <el-table-column prop="adMonetizationCost" label="广告变现成本" min-width="115" align="right" sortable="custom">
          <template slot-scope="scope">{{ moneyText(scope.row.adMonetizationCost) }}</template>
        </el-table-column>
        <el-table-column prop="payUsers" label="付费人数" min-width="90" align="right" sortable="custom"></el-table-column>
        <el-table-column prop="payIncome" label="付费金额" min-width="95" align="right" sortable="custom">
          <template slot-scope="scope">{{ moneyText(scope.row.payIncome) }}</template>
        </el-table-column>
        <el-table-column prop="realProfit" label="实时利润" min-width="105" align="right" sortable="custom">
          <template slot-scope="scope"><span :class="profitClass(scope.row.realProfit)">{{ moneyText(scope.row.realProfit) }}</span></template>
        </el-table-column>
        </el-table>
      </div>
      <div class="pagination-row">
        <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page.sync="page.current"
            :page-size.sync="page.size"
            :page-sizes="[20, 50, 100, 200]"
            :total="sortedRows.length">
        </el-pagination>
      </div>
    </section>

    <el-dialog
        title="负责人账号配置"
        :visible.sync="ownerDialogVisible"
        width="820px"
        append-to-body
        class="owner-config-dialog">
      <div class="owner-config-panel">
        <el-form :inline="true" size="mini" class="owner-config-form">
          <el-form-item label="负责人">
            <el-input v-model.trim="ownerForm.ownerName" clearable placeholder="请输入负责人" class="owner-name-input"></el-input>
          </el-form-item>
          <el-form-item label="账号">
            <el-select
                v-model="ownerForm.accountNames"
                multiple
                filterable
                collapse-tags
                placeholder="请选择账号"
                class="owner-account-select">
              <el-option
                  v-for="item in accountOptions"
                  :key="item"
                  :label="item"
                  :value="item">
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="启用">
            <el-switch v-model="ownerForm.enabled"></el-switch>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" :loading="ownerSaving" @click="saveOwnerConfig">保存</el-button>
            <el-button @click="resetOwnerForm">清空</el-button>
          </el-form-item>
        </el-form>
        <el-table
            :data="ownerConfigs"
            border
            stripe
            size="mini"
            max-height="420"
            v-loading="ownerConfigLoading">
          <el-table-column prop="ownerName" label="负责人" width="120" show-overflow-tooltip></el-table-column>
          <el-table-column prop="accountCount" label="账号数" width="80" align="right"></el-table-column>
          <el-table-column label="账号">
            <template slot-scope="scope">
              <div class="owner-account-tags">
                <el-tag v-for="name in scope.row.accountNames" :key="name" size="mini" type="info">{{ name }}</el-tag>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="enabled" label="状态" width="80" align="center">
            <template slot-scope="scope">
              <el-tag size="mini" :type="scope.row.enabled ? 'success' : 'info'">{{ scope.row.enabled ? '启用' : '停用' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="updatedAt" label="更新时间" width="155" show-overflow-tooltip></el-table-column>
          <el-table-column label="操作" width="130" align="center">
            <template slot-scope="scope">
              <el-button type="text" size="mini" @click="editOwnerConfig(scope.row)">编辑</el-button>
              <el-button type="text" size="mini" class="danger-text" @click="deleteOwnerConfig(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-dialog>
  </div>
</template>

<script>
function todayRange() {
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  return [now.getTime(), now.getTime()]
}

export default {
  name: 'VideoBoostStatistics',
  props: {
    mode: {
      type: String,
      default: 'account'
    }
  },
  data() {
    return {
      loading: false,
      filters: {
        dateRange: todayRange(),
        owner: '',
        account: '',
        game: '',
        gameType: '',
        status: 0,
        keyword: ''
      },
      rows: [],
      summary: {},
      meta: {},
      accountOptions: [],
      ownerOptions: [],
      ownerByAccount: {},
      ownerConfigs: [],
      ownerDialogVisible: false,
      ownerConfigLoading: false,
      ownerSaving: false,
      ownerForm: {
        ownerName: '',
        accountNames: [],
        enabled: true
      },
      gameOptions: [],
      gameTypeOptions: [],
      page: {
        current: 1,
        size: 50
      },
      sort: {
        prop: 'actualCost',
        order: 'descending'
      }
    }
  },
  computed: {
    isGameMode() {
      return this.mode === 'game'
    },
    filteredAccountOptions() {
      if (!this.filters.owner) {
        return this.accountOptions
      }
      return this.accountOptions.filter(item => this.ownerByAccount[item] === this.filters.owner)
    },
    sortedRows() {
      const rows = this.rows.slice()
      const prop = this.sort.prop
      const direction = this.sort.order === 'ascending' ? 1 : -1
      if (!prop || !this.sort.order) {
        return rows
      }
      return rows.sort((left, right) => {
        const a = left[prop]
        const b = right[prop]
        if (typeof a === 'number' || typeof b === 'number') {
          return (Number(a || 0) - Number(b || 0)) * direction
        }
        return String(a || '').localeCompare(String(b || ''), 'zh-Hans-CN') * direction
      })
    },
    pagedRows() {
      const start = (this.page.current - 1) * this.page.size
      return this.sortedRows.slice(start, start + this.page.size)
    },
    failedAccountText() {
      const values = Array.isArray(this.meta.failedAccounts) ? this.meta.failedAccounts : []
      return values.slice(0, 5).join('；') + (values.length > 5 ? '；其余 ' + (values.length - 5) + ' 个请查看服务日志' : '')
    }
  },
  mounted() {
    this.applyRouteFilters()
    this.loadOwnerConfig()
    this.queryData()
  },
  activated() {
    if (this.applyRouteFilters()) {
      this.queryData()
    }
  },
  watch: {
    '$route.query'() {
      if (this.applyRouteFilters()) {
        this.queryData()
      }
    }
  },
  methods: {
    applyRouteFilters() {
      const query = (this.$route && this.$route.query) || {}
      if (!query || Object.keys(query).length === 0) {
        return false
      }
      const next = Object.assign({}, this.filters)
      if (query.start != null || query.end != null) {
        const start = Number(query.start)
        const end = Number(query.end)
        if (Number.isFinite(start) && Number.isFinite(end) && start > 0 && end > 0) {
          next.dateRange = [start, end]
        }
      }
      if (query.status != null) {
        const status = Number(query.status)
        next.status = Number.isFinite(status) ? status : 0
      }
      if (query.jump === 'game') {
        next.owner = ''
        next.account = ''
        next.game = this.cleanJumpValue(query.game)
        next.gameType = ''
        next.keyword = ''
      } else if (query.jump === 'account') {
        next.owner = ''
        next.account = this.cleanJumpValue(query.account)
        next.game = ''
        next.gameType = ''
        next.keyword = ''
      } else {
        if (query.owner != null) next.owner = this.cleanJumpValue(query.owner)
        if (query.account != null) next.account = this.cleanJumpValue(query.account)
        if (query.game != null) next.game = this.cleanJumpValue(query.game)
        if (query.gameType != null) next.gameType = this.cleanJumpValue(query.gameType)
        if (query.keyword != null) next.keyword = this.cleanJumpValue(query.keyword)
      }
      const changed = JSON.stringify(this.filters) !== JSON.stringify(next)
      if (changed) {
        this.filters = next
        this.page.current = 1
      }
      return changed
    },
    async loadOwnerConfig() {
      this.ownerConfigLoading = true
      try {
        const response = await this.$axios.get('/data/zhibo/account-owner-config')
        const data = response.data && response.data.success ? response.data.data : {}
        const accounts = Array.isArray(data.accounts) ? data.accounts : []
        this.accountOptions = accounts
            .filter(item => Number(item.status) === 0 && item.name)
            .map(item => item.name)
        this.ownerConfigs = Array.isArray(data.configs) ? data.configs : []
        this.ownerOptions = Array.isArray(data.owners) ? data.owners : []
        this.rebuildOwnerMap()
      } catch (error) {
        this.accountOptions = []
        this.ownerConfigs = []
        this.ownerOptions = []
        this.ownerByAccount = {}
      } finally {
        this.ownerConfigLoading = false
      }
    },
    async queryData() {
      if (!Array.isArray(this.filters.dateRange) || this.filters.dateRange.length !== 2) {
        this.$message.warning('请选择查询日期')
        return
      }
      this.loading = true
      try {
        const params = {
          start: this.filters.dateRange[0],
          end: this.filters.dateRange[1] + 86400000 - 1,
          stats: this.filters.status,
          owner: this.filters.owner || undefined,
          account: this.filters.account || undefined,
          game: this.filters.game || undefined,
          gameType: this.filters.gameType || undefined,
          keyword: this.filters.keyword || undefined
        }
        const endpoint = this.isGameMode ? '/data/zhibo/statistics/games' : '/data/zhibo/statistics/accounts'
        const response = await this.$axios.get(endpoint, {params})
        if (!response.data || !response.data.success) {
          throw new Error((response.data && response.data.msg) || '汇总查询失败')
        }
        const data = response.data.data || {}
        this.rows = Array.isArray(data.rows) ? data.rows : []
        this.summary = data.summary || {}
        this.meta = data.meta || {}
        const options = data.options || {}
        if (Array.isArray(options.accounts) && options.accounts.length) {
          this.accountOptions = options.accounts
        }
        if (Array.isArray(options.owners)) {
          this.ownerOptions = options.owners
        }
        if (options.ownerByAccount && typeof options.ownerByAccount === 'object') {
          this.ownerByAccount = options.ownerByAccount
        }
        this.gameOptions = Array.isArray(options.games) ? options.games : []
        this.gameTypeOptions = Array.isArray(options.gameTypes) ? options.gameTypes : []
        this.page.current = 1
      } catch (error) {
        this.rows = []
        this.summary = {}
        this.meta = {}
        this.$message.error(error.message || '汇总查询失败')
      } finally {
        this.loading = false
      }
    },
    resetFilters() {
      this.filters = {
        dateRange: todayRange(),
        owner: '',
        account: '',
        game: '',
        gameType: '',
        status: 0,
        keyword: ''
      }
      this.queryData()
    },
    handleOwnerChange() {
      if (this.filters.account && !this.filteredAccountOptions.includes(this.filters.account)) {
        this.filters.account = ''
      }
    },
    rebuildOwnerMap() {
      const ownerByAccount = {}
      const owners = new Set(this.ownerOptions)
      this.ownerConfigs.forEach(item => {
        if (item && item.ownerName) {
          owners.add(item.ownerName)
          const accounts = Array.isArray(item.accountNames) ? item.accountNames : []
          accounts.forEach(accountName => {
            if (accountName) {
              ownerByAccount[accountName] = item.ownerName
            }
          })
        }
      })
      this.ownerByAccount = ownerByAccount
      this.ownerOptions = Array.from(owners).filter(Boolean).sort((a, b) => String(a).localeCompare(String(b), 'zh-Hans-CN'))
    },
    openOwnerDialog() {
      this.ownerDialogVisible = true
      this.loadOwnerConfig()
    },
    resetOwnerForm() {
      this.ownerForm = {
        ownerName: '',
        accountNames: [],
        enabled: true
      }
    },
    editOwnerConfig(row) {
      this.ownerForm = {
        ownerName: row.ownerName || '',
        accountNames: Array.isArray(row.accountNames) ? row.accountNames.slice() : [],
        enabled: row.enabled !== false
      }
    },
    async saveOwnerConfig() {
      if (!this.ownerForm.ownerName) {
        this.$message.warning('请输入负责人')
        return
      }
      if (!Array.isArray(this.ownerForm.accountNames) || !this.ownerForm.accountNames.length) {
        this.$message.warning('请选择账号')
        return
      }
      this.ownerSaving = true
      try {
        const response = await this.$axios.post('/data/zhibo/account-owner-config', this.ownerForm)
        if (!response.data || !response.data.success) {
          throw new Error((response.data && response.data.msg) || '负责人配置保存失败')
        }
        this.$message.success('负责人配置已保存')
        this.resetOwnerForm()
        await this.loadOwnerConfig()
        this.handleOwnerChange()
      } catch (error) {
        this.$message.error(error.message || '负责人配置保存失败')
      } finally {
        this.ownerSaving = false
      }
    },
    async deleteOwnerConfig(row) {
      try {
        await this.$confirm('确认删除负责人“' + row.ownerName + '”的账号配置吗？', '删除确认', {type: 'warning'})
        const response = await this.$axios.post('/data/zhibo/account-owner-config/delete', {ownerName: row.ownerName})
        if (!response.data || !response.data.success) {
          throw new Error((response.data && response.data.msg) || '负责人配置删除失败')
        }
        this.$message.success('负责人配置已删除')
        if (this.filters.owner === row.ownerName) {
          this.filters.owner = ''
          this.filters.account = ''
        }
        await this.loadOwnerConfig()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error(error.message || '负责人配置删除失败')
        }
      }
    },
    handleSortChange({prop, order}) {
      this.sort = {prop, order}
      this.page.current = 1
    },
    routeQueryBase() {
      const range = Array.isArray(this.filters.dateRange) && this.filters.dateRange.length === 2
          ? this.filters.dateRange
          : todayRange()
      return {
        start: String(range[0]),
        end: String(range[1]),
        status: String(this.filters.status || 0)
      }
    },
    routeQueryForRow(row) {
      const query = this.routeQueryBase()
      if (!this.isGameMode || !row || !row.date) {
        return query
      }
      const dayStart = this.toDayTimestamp(row.date)
      if (dayStart != null) {
        query.start = String(dayStart)
        query.end = String(dayStart)
      }
      return query
    },
    toDayTimestamp(value) {
      const text = String(value == null ? '' : value).trim()
      if (!text) {
        return null
      }
      const parts = text.split('-').map(part => Number(part))
      if (parts.length < 3 || parts.some(part => !Number.isFinite(part))) {
        return null
      }
      const date = new Date(parts[0], parts[1] - 1, parts[2])
      date.setHours(0, 0, 0, 0)
      return date.getTime()
    },
    cleanJumpValue(value) {
      const text = String(value == null ? '' : value).trim()
      return text === '-' ? '' : text
    },
    isValidJumpValue(value) {
      const text = this.cleanJumpValue(value)
      return !!text && text !== '未识别游戏' && text !== '合计'
    },
    splitAccountNames(value) {
      return String(value == null ? '' : value)
          .split(/[、,，;；]+/)
          .map(item => item.trim())
          .filter(item => item && item !== '-')
    },
    canJumpToOrderDashboard(row) {
      return !!(row && Number(row.orderCount || 0) > 0 && this.isValidJumpValue(row.gameName))
    },
    goToOrderDashboard(row) {
      if (!this.canJumpToOrderDashboard(row)) {
        return
      }
      const game = this.cleanJumpValue(row.gameName)
      const accounts = this.splitAccountNames(row.accountName).join(',')
      const query = Object.assign({}, this.routeQueryForRow(row), {
        jump: 'order-game',
        game
      })
      if (accounts) {
        query.accounts = accounts
      }
      this.$router.push({
        path: '/VideoBoostOrders',
        query
      }).catch(() => {})
    },
    goToGameStats(gameName) {
      const game = this.cleanJumpValue(gameName)
      if (!game) {
        return
      }
      this.$router.push({
        path: '/VideoBoostGameStats',
        query: Object.assign({}, this.routeQueryBase(), {
          jump: 'game',
          game
        })
      }).catch(() => {})
    },
    goToAccountStats(accountName) {
      const account = this.cleanJumpValue(accountName)
      if (!account) {
        return
      }
      this.$router.push({
        path: '/VideoBoostAccountStats',
        query: Object.assign({}, this.routeQueryBase(), {
          jump: 'account',
          account
        })
      }).catch(() => {})
    },
    rowIndex(index) {
      return (this.page.current - 1) * this.page.size + index + 1
    },
    tableSummary({columns}) {
      return columns.map((column, index) => {
        if (index === 0) return '合计'
        const prop = column.property
        if (!prop) return ''
        const countFields = ['orderCount', 'adUsers', 'payUsers']
        const moneyFields = [
          'orderBudget', 'bookCost', 'actualCost', 'activationCost', 'firstDayArpu',
          'firstDayMonetizationAmount', 'voucherAmount', 'adIncome', 'payIncome', 'realProfit'
        ]
        if (countFields.includes(prop)) return this.numberText(this.summary[prop], 0)
        if (moneyFields.includes(prop)) return this.moneyText(this.summary[prop])
        if (prop === 'costProgress') return this.percentText(this.summary.costProgress)
        if (prop === 'firstDayRoiAdjusted' || prop === 'realtimeRoi') return this.roiText(this.summary[prop])
        return ''
      })
    },
    numberText(value, digits) {
      const number = Number(value || 0)
      return number.toLocaleString('zh-CN', {minimumFractionDigits: digits, maximumFractionDigits: digits})
    },
    moneyText(value) {
      return this.numberText(value, 2)
    },
    percentText(value) {
      return this.numberText(value, 2) + '%'
    },
    progressPercentage(value) {
      return Math.min(100, Math.max(0, Number(value || 0)))
    },
    progressColor(value) {
      const progress = Number(value || 0)
      if (progress >= 100) return '#e6a23c'
      if (progress >= 80) return '#67c23a'
      return '#409eff'
    },
    roiText(value) {
      return this.numberText(value, 4)
    },
    profitClass(value) {
      return Number(value || 0) >= 0 ? 'profit-positive' : 'profit-negative'
    },
    exportRows() {
      const columns = this.isGameMode
          ? [['日期', 'date'], ['游戏', 'gameName'], ['品类', 'gameType'], ['负责人', 'ownerName'], ['账号数', 'accountCount'], ['投放账号', 'accountName']]
          : [['账号', 'accountName'], ['负责人', 'ownerName'], ['账号ID', 'corporateAccountId'], ['游戏', 'gameName'], ['品类', 'gameType'], ['账户余额', 'accountBalance']]
      columns.push(
          ['订单量', 'orderCount'], ['订单总预算', 'orderBudget'], ['账面消耗', 'bookCost'], ['消耗进度', 'costProgress'], ['实际消耗', 'actualCost'],
          ['激活成本', 'activationCost'], ['首日ARPU', 'firstDayArpu'], ['首日变现金额', 'firstDayMonetizationAmount'],
          ['首日ROI（赔付后）', 'firstDayRoiAdjusted'], ['实时变现ROI', 'realtimeRoi'], ['赔付券', 'voucherAmount'],
          ['广告变现人数', 'adUsers'], ['广告变现金额', 'adIncome'], ['广告变现成本', 'adMonetizationCost'],
          ['付费人数', 'payUsers'], ['付费金额', 'payIncome'], ['实时利润', 'realProfit'])
      const escape = value => '"' + String(value == null ? '' : value).replace(/"/g, '""') + '"'
      const lines = [columns.map(item => escape(item[0])).join(',')]
      this.sortedRows.forEach(row => lines.push(columns.map(item => escape(row[item[1]])).join(',')))
      const blob = new Blob(['\ufeff' + lines.join('\r\n')], {type: 'text/csv;charset=utf-8'})
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = (this.isGameMode ? '视频号加热游戏看板' : '视频号加热账号看板') + '.csv'
      link.click()
      URL.revokeObjectURL(link.href)
    }
  }
}
</script>

<style scoped>
.video-boost-stat-page {
  height: 100vh;
  padding: 16px 20px 18px;
  box-sizing: border-box;
  background: #f1f4f8;
  color: #252b36;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: hidden;
}

.stat-toolbar,
.stat-table-section {
  background: #fff;
  border: 1px solid #e5eaf1;
  border-radius: 6px;
  box-sizing: border-box;
}

.stat-toolbar {
  padding: 14px 16px 12px;
}

.toolbar-title {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 12px;
}

.toolbar-title h2 {
  margin: 0;
  font-size: 19px;
  line-height: 26px;
  letter-spacing: 0;
}

.toolbar-title span,
.table-meta {
  color: #8992a3;
  font-size: 12px;
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.date-filter { width: 240px; }
.filter-control { width: 150px; }
.status-filter { width: 120px; }
.keyword-filter { width: 190px; }

.failure-alert {
  flex: 0 0 auto;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(130px, 1fr));
  gap: 10px;
}

.metric-item {
  min-height: 74px;
  background: #fff;
  border: 1px solid #e5eaf1;
  border-left: 3px solid #7c8797;
  border-radius: 6px;
  padding: 12px 14px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
}

.metric-item span { color: #7d8796; font-size: 12px; }
.metric-item strong { font-size: 20px; line-height: 24px; }
.metric-blue { border-left-color: #409eff; }
.metric-orange { border-left-color: #e6a23c; }
.metric-green { border-left-color: #32a06f; }

.stat-table-section {
  flex: 1;
  min-height: 0;
  padding: 10px 12px 8px;
  display: flex;
  flex-direction: column;
}

.table-meta {
  flex: 0 0 26px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.table-body {
  flex: 1;
  min-height: 0;
}

.table-body ::v-deep .el-table {
  min-height: 320px;
}

.stat-table-section ::v-deep th.el-table__cell {
  background: #f6f8fb;
  color: #4d5665;
  font-weight: 600;
}

.stat-table-section ::v-deep .el-table__footer-wrapper td {
  background: #eef5ff;
  font-weight: 600;
  color: #303846;
}

.pagination-row {
  flex: 0 0 40px;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
}

.progress-cell {
  display: grid;
  grid-template-columns: minmax(58px, 1fr) 54px;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.progress-cell .el-progress {
  width: 100%;
}

.progress-cell span {
  color: #4d5665;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.link-cell {
  max-width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: #409eff;
  font: inherit;
  line-height: 18px;
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.link-cell:hover {
  color: #1677d2;
  text-decoration: underline;
}

.link-list-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  max-height: 58px;
  overflow: auto;
  align-items: flex-start;
}

.link-list-cell .link-cell {
  flex: 0 1 auto;
}

.order-count-link {
  display: inline-block;
  width: 100%;
  text-align: right;
}

.owner-config-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.owner-config-form {
  padding: 10px 12px 2px;
  background: #f7f9fc;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.owner-name-input {
  width: 150px;
}

.owner-account-select {
  width: 360px;
}

.owner-account-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  max-height: 72px;
  overflow: auto;
}

.danger-text {
  color: #f56c6c;
}

.profit-positive { color: #25865f; font-weight: 600; }
.profit-negative { color: #d64f4f; font-weight: 600; }

@media (max-width: 1280px) {
  .metric-grid { grid-template-columns: repeat(3, minmax(140px, 1fr)); }
  .video-boost-stat-page { overflow: auto; height: auto; min-height: 100vh; }
  .stat-table-section { height: 620px; }
}
</style>
